import * as T from "@shared/types";

import React from "react";
import i18n from '@client-shared/lib/i18n';
import { createIndividualVariantProducts } from '@client-shared/lib/util';
import * as api from '@client-shared/services/api';
import { handleDisplayErrorData } from '../../services/api/utilities/handleDisplayErrorData';
import { useConfigState } from "../config/useConfigState";

import { setSingleParentProducts } from "@client-shared/utilities/productSyncUtilities";
import { mapAllProducts } from "./utilities/productDataTransformations";
import { SalesforceProduct } from "@shared/index";
import { useSFDCAuthState } from "../auth/useSFDCAuthState";
import useVendorContext from "../vendor/useCurrentVendor";
import {
  addSkuImportDraft,
  editSkuImportDrafts,
  reducer,
  setAllRemoteFormProducts,
  setConnections,
  setError,
  setFilteredConnections,
  setFilteredLocalProducts,
  setFilteredRemoteProducts,
  setInitialized,
  setLoading,
  setLocalParentChildProductsAction,
  setLocalProducts,
  setRemoteFormProductsWithImports,
  setRemoteProducts,
  setServiceProducts,
  setSkuImportDrafts,
} from "./actions";
import { ProductsActions, ProductsContextState, ProductsState } from "./types";

export const initialState: ProductsState = {
  initialized: false,
  installationId: undefined,
  remoteProducts: undefined,
  remoteFormProducts: undefined,
  remoteFormProductsWithImported: undefined,
  skuImportDrafts: undefined,
  filteredRemoteProducts: undefined,
  localProducts: undefined,
  filteredLocalProducts: undefined,
  serviceProducts: undefined,
  connections: undefined,
  filteredConnections: undefined,
  localParentChildProducts: undefined,
  loading: {
    remoteProducts: true,
    localProducts: true,
    serviceProducts: true,
    connections: true,
    skuImportDrafts: true,
  },
  error: {
    remoteProducts: undefined,
    localProducts: undefined,
    serviceProducts: undefined,
    connections: undefined,
    skuImportDrafts: undefined,
  },
};

export const defaultContextState: ProductsContextState = [initialState, () => undefined];

export const ProductsContext = React.createContext(defaultContextState);

type Props = {
  children: React.ReactNode;
};

export const ProductsProvider: React.FC<Props> = ({ children }: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [authState] = useSFDCAuthState();
  const [{ initialized: isVendorInitialized, currentVendor }] = useVendorContext();
  const [currentInstallationId, setCurrentInstallationId] = React.useState<string | undefined>(undefined);
  // initialize the context

  const [{ config }, ,] = useConfigState();
  const {
    productConfig,
    allBagSizesConfig,
    allGrindTypesConfig,
    defaultRegionLocalisedProductConfig,
    selectedLocalisedProductConfig,
    regionLargeBagSize,
    roasterNoteMaxLength,
    titleMaxLength,
  } = config;

  React.useEffect(() => {
    if (!isVendorInitialized || !authState || state.initialized || !currentInstallationId) return;
    if (!state.initialized) {
      dispatch(setInitialized(true));
    }
  }, [authState, currentInstallationId, isVendorInitialized, state.initialized]);

  // get the connections
  React.useEffect(() => {
    if (!state.initialized || !currentInstallationId || !state.loading.connections) return;
    fetchConnections(currentInstallationId, dispatch);
  }, [currentInstallationId, state.initialized, state.loading.connections]);

  // set the filtered connections
  React.useEffect(() => {
    if (!state.initialized || !state.connections || state.filteredConnections) return;
    dispatch(setFilteredConnections(transformToFilteredConnections(state.connections)));
  }, [state.connections, state.initialized, state.filteredConnections]);

  // get the local products
  React.useEffect(() => {
    if (!state.initialized || !currentVendor?.vendorKey || !state.loading.localProducts) return;
    fetchLocalProducts(currentVendor, dispatch);
  }, [currentVendor, currentVendor?.vendorKey, state.initialized, state.loading.localProducts]);

  // set the filtered local products
  React.useEffect(() => {
    if (!state.initialized || !state.filteredConnections || !state.localProducts || state.filteredLocalProducts) return;
    dispatch(setFilteredLocalProducts(transformToFilteredProducts(state.filteredConnections, state.localProducts)));
  }, [state.filteredConnections, state.initialized, state.localProducts, state.filteredLocalProducts]);

  // get the remote products
  React.useEffect(() => {
    if (!state.initialized || !currentVendor?.vendorKey || !currentInstallationId || !state.loading.remoteProducts) return;
    fetchRemoteProducts(currentInstallationId, currentVendor, dispatch);
  }, [currentVendor, currentVendor?.vendorKey, state.initialized, currentInstallationId, state.loading.remoteProducts]);

  // get the filtered remote products
  React.useEffect(() => {
    if (!state.initialized || !state.filteredConnections || !state.remoteProducts || state.filteredRemoteProducts) return;
    dispatch(setFilteredRemoteProducts(transformToFilteredLocalProducts(state.filteredConnections, state.remoteProducts)));
  }, [state.filteredConnections, state.initialized, state.remoteProducts, state.filteredRemoteProducts]);

  // get the sku import drafts
  React.useEffect(() => {
    if (!state.initialized || !currentInstallationId || !state.loading.skuImportDrafts) return;
    fetchSkuImportDrafts(currentInstallationId, dispatch);
  }, [currentInstallationId, state.initialized, state.loading.skuImportDrafts]);

  // get the service products
  React.useEffect(() => {
    if (!state.initialized || !currentVendor?.vendorKey || !state.loading.serviceProducts) return;

    fetchServiceProducts(currentVendor, dispatch);
  }, [authState, currentVendor, currentVendor?.vendorKey, state.initialized, state.loading.serviceProducts]);

  const handleSetAllRemoteFormProducts = React.useCallback(
    (hasImports: boolean) => {
      if (
        !state.filteredRemoteProducts ||
        !state.remoteProducts ||
        !productConfig ||
        !selectedLocalisedProductConfig ||
        !defaultRegionLocalisedProductConfig ||
        !state.filteredLocalProducts ||
        !state.filteredConnections
      ) {
        return [];
      }

      // if hasImports then use imports and normal data
      // if not use filtered data
      return mapAllProducts({
        products: hasImports ? state.remoteProducts : state.filteredRemoteProducts,
        productConfig,
        importedProducts: hasImports ? state.serviceProducts : undefined,
        localProducts: hasImports ? state.localProducts : state.filteredLocalProducts,
        connections: hasImports ? state.connections : state.filteredConnections,
        allBagSizesConfig,
        allGrindTypesConfig,
        localisedProductConfig: defaultRegionLocalisedProductConfig,
        roasterNoteMaxLength,
        titleMaxLength,
        regionLargeBagSize,
      });
    },
    [
      state.filteredRemoteProducts,
      state.filteredLocalProducts,
      state.filteredConnections,
      state.remoteProducts,
      state.serviceProducts,
      state.localProducts,
      state.connections,
      productConfig,
      selectedLocalisedProductConfig,
      defaultRegionLocalisedProductConfig,
      allBagSizesConfig,
      allGrindTypesConfig,
      roasterNoteMaxLength,
      titleMaxLength,
      regionLargeBagSize,
    ]
  );
  React.useEffect(() => {
    if (
      !state.filteredRemoteProducts ||
      !productConfig ||
      !selectedLocalisedProductConfig ||
      !defaultRegionLocalisedProductConfig ||
      !state.filteredLocalProducts ||
      !state.filteredConnections
    ) {
      return;
    }
    const remoteFormProductsWithImports = handleSetAllRemoteFormProducts(true);
    console.log(remoteFormProductsWithImports.length);
    dispatch(setRemoteFormProductsWithImports(remoteFormProductsWithImports));
    const remoteFormProducts = handleSetAllRemoteFormProducts(false);
    console.log(remoteFormProducts.length);
    dispatch(setAllRemoteFormProducts(remoteFormProducts));
  }, [
    handleSetAllRemoteFormProducts,
    state.filteredRemoteProducts,
    productConfig,
    selectedLocalisedProductConfig,
    state.filteredLocalProducts,
    state.filteredConnections,
    state.loading.remoteProducts,
    state.loading.localProducts,
    state.loading.serviceProducts,
    state.loading.connections,
    defaultRegionLocalisedProductConfig,
  ]);

  React.useEffect(() => {
    if (!state.localProducts || state.localProducts.length === 0) return;
    const localParentChildProducts = setSingleParentProducts(state.localProducts);
    if (!localParentChildProducts || localParentChildProducts.length === 0) return;
    dispatch(setLocalParentChildProductsAction(localParentChildProducts));
  }, [state.localProducts]);

  const handleSetInstallationId = React.useCallback(
    (installationId: string) => {
      if (currentInstallationId === installationId) return;
      setCurrentInstallationId(installationId);
    },
    [currentInstallationId]
  );
  ///////////////////////////////////////////////////////////////
  // utilities
  //

  const handlePostSkuImportDraft = React.useCallback(
    async (product: T.FormProduct) => {
      if (!currentVendor || !currentInstallationId) return;
      const newDraft = await postSkuImportDraft(product, currentVendor, currentInstallationId);
      if (!newDraft) return;
      dispatch(addSkuImportDraft(newDraft));
    },
    [currentInstallationId, currentVendor]
  );

  const handleUpdateSkuImportDrafts = React.useCallback(
    async (products: T.FormProduct[], status: T.SkuImportDraftState) => {
      if (!currentVendor || !currentInstallationId) return;

      const newDrafts = await updateSkuImportDrafts(products, currentVendor, currentInstallationId, status); //
      if (!newDrafts) return;
      dispatch(editSkuImportDrafts(newDrafts, status));
    },
    [currentInstallationId, currentVendor]
  );

  // context value
  const contextValue: ProductsContextState = React.useMemo(
    () => [
      state,
      dispatch,
      {
        handleInstallationId: handleSetInstallationId,
        handlePostSkuImportDraft: handlePostSkuImportDraft,
        handleUpdateSkuImportDrafts: handleUpdateSkuImportDrafts,
        handlePostProducts: postProducts,
      },
    ],
    [state, handleSetInstallationId, handlePostSkuImportDraft, handleUpdateSkuImportDrafts]
  );
  return <ProductsContext.Provider value={contextValue}>{children}</ProductsContext.Provider>;
};

//sideEffects
const fetchConnections = async (installationId: string, dispatch: (action: ProductsActions) => void) => {
  try {
    const variantConnections = await api.getInstallationProductConnections(installationId);
    if (!variantConnections) return;
    dispatch(setConnections(variantConnections));
    const filteredVariantConnections = variantConnections.filter((v) => v.state !== "DELETED" && v.state !== "PAUSED");
    dispatch(setFilteredConnections(filteredVariantConnections));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchConnections.message"),
      i18n.t("apiErrorMessages.fetchConnections.title"),
      e as Error
    );
    dispatch(setError("connections", e as Error));
  } finally {
    dispatch(setLoading("connections", false));
  }
};

const fetchRemoteProducts = async (installationId: string, currentVendor: T.Vendor, dispatch: (action: ProductsActions) => void) => {
  try {
    const remoteProducts = await api.getInstallationProducts(currentVendor, installationId);
    if (!remoteProducts) return;
    dispatch(setRemoteProducts(remoteProducts));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchRemoteProducts.message"),
      i18n.t("apiErrorMessages.fetchRemoteProducts.title"),
      e as Error
    );
    dispatch(setError("remoteProducts", e as Error));
  } finally {
    dispatch(setLoading("remoteProducts", false));
  }
};

const fetchSkuImportDrafts = async (installationId: string, dispatch: (action: ProductsActions) => void) => {
  try {
    const skuImportDrafts = await api.getSkuImportDrafts(installationId);
    if (!skuImportDrafts) return [];
    dispatch(setSkuImportDrafts(skuImportDrafts?.map((draft) => draft.draftFormProduct)));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchSkuImportProducts.message"),
      i18n.t("apiErrorMessages.fetchSkuImportProducts.title"),
      e as Error
    );
    dispatch(setError("skuImportDrafts", e as Error));
  } finally {
    dispatch(setLoading("skuImportDrafts", false));
  }
};

const postSkuImportDraft = async (product: T.FormProduct, vendor: T.Vendor, installationId: string) => {
  try {
    await api.postSkuImportDrafts(vendor, installationId, [product]);
    return product;
  } catch (e) {
    handleDisplayErrorData("error with posting draft", "someError in drafts", e as Error);
  }
};

const updatedSingleSkuImportDraft = async (
  product: T.FormProduct,
  vendor: T.Vendor,
  installationId: string,
  status: T.SkuImportDraftState
) => {
  await api.patchSkuImportDrafts(vendor, installationId, product, status);
  return { ...product, draftState: status } as T.FormProduct;
};

const updateSkuImportDrafts = async (
  products: T.FormProduct[],
  vendor: T.Vendor,
  installationId: string,
  status: T.SkuImportDraftState
) => {
  try {
    await Promise.all(products.map((product) => updatedSingleSkuImportDraft(product, vendor, installationId, status)));
    return products;
  } catch (e) {
    handleDisplayErrorData("error with updating drafts", "someError in drafts", e as Error);
  }
};

const fetchLocalProducts = async (currentVendor: T.Vendor, dispatch: (action: ProductsActions) => void) => {
  try {
    const localProducts = await api.getServiceProducts(currentVendor, "pim");
    if (!localProducts) return;
    dispatch(setLocalProducts(createIndividualVariantProducts(localProducts)));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchLocalProducts.message"),
      i18n.t("apiErrorMessages.fetchLocalProducts.title"),
      e as Error
    );
    dispatch(setError("localProducts", e as Error));
  } finally {
    dispatch(setLoading("localProducts", false));
  }
};

const fetchServiceProducts = async (currentVendor: T.Vendor, dispatch: (action: ProductsActions) => void) => {
  try {
    const serviceProducts = await api.getServiceProducts(currentVendor, "salesforce");
    if (!serviceProducts) return;
    dispatch(setServiceProducts([...new Set(serviceProducts)]));
  } catch (e) {
    handleDisplayErrorData(
      i18n.t("apiErrorMessages.fetchServiceProducts.message"),
      i18n.t("apiErrorMessages.fetchServiceProducts.title"),
      e as Error
    );
    dispatch(setError("serviceProducts", e as Error));
  } finally {
    dispatch(setLoading("serviceProducts", false));
  }
};

// data transformation

export const transformToFilteredConnections = (connections: T.ProductConnection[]) => {
  return connections.filter((v) => v.state !== "DELETED" && v.state !== "PAUSED");
};

export const transformToFilteredLocalProducts = (filteredConnections: T.ProductConnection[], localProducts: T.Product[]) => {
  const connectedLocalVariantIds = new Set(filteredConnections.map((connection) => connection.sourceObjectId));
  const individualVariantProducts = localProducts ? createIndividualVariantProducts(localProducts) : [];
  const filteredLocalProducts = individualVariantProducts.map((product) => ({
    ...product,
    variants: product.variants?.filter((variant) => !connectedLocalVariantIds.has(variant.id)),
  }));
  return filteredLocalProducts;
};

export const transformToFilteredProducts = (filteredConnections: T.ProductConnection[], remoteProducts: T.Product[]) => {
  const connectedRemoteVariantIds = new Set(filteredConnections.map((connection) => connection.targetVariantId));
  const filteredRemoteProducts = remoteProducts.map((product) => ({
    ...product,
    variants: product.variants?.filter((variant) => !connectedRemoteVariantIds.has(variant.id)),
  }));
  return filteredRemoteProducts;
};

export const postProducts = async (serviceId: string, accessToken: string, products: Partial<SalesforceProduct>[]) => {
  await api.postProducts(serviceId, accessToken, products);
};
